import dayjs from 'dayjs';
import { Tag, Tooltip } from 'antd';
import { memo, useMemo, useState } from 'react';
import { faCalendar, faFlask, faMap, faSeedling, faUsers, faUsersLine, faVihara } from '@fortawesome/free-solid-svg-icons';

//APP
import { useFormOsContext } from '../../context/FormOsContext';
import { statusDesabilitados } from '../../../../meta/statusTimeline';
import { dados_status, statusIcones, statusNomes, STATUS_COLETADA, STATUS_ENVIADO_ANALISE } from '../../../../meta';

//COMPONENTS
import Tabs from '../../../../../../../../../../Components/Tabs';
import Input from '../../../../../../../../../../Components/CustomUi/input';
import DatePickerCustom from '../../../../../../../../../../Components/CustomUi/datePicker';
import { Render } from '../../../../../../../../../../Components/CustomUi/render';
import { Select } from '../../../../../../../../../../Components/CustomUi/Select';
import { SelectMulti } from '../../../../../../../../../../Components/CustomUi/SelectMulti';

const AREAS_AMOSTRA = [
    { icone: '', descricao: '50 ha' },
    { icone: '', descricao: '20 ha' },
]

/**
 * Metade esquerda do formulário: campos de cadastro da OS.
 * Consome o estado via context (sem prop drilling). Memoizado para não
 * re-renderizar junto com a metade "Análises".
 */
export const CamposFormulario = memo(() => {

    const {
        campos,
        setCampo,
        setResponsaveis,
        selecionarPropriedade,
        selecionarPropriedadeId,
        matricula,
        area_propriedade,
        talhoes,
        carregando_talhoes,
        propriedades_ativas,
        carregando_propriedades,
        data_safra_consultoria,
        data_equipe,
        data_laboratorios,
        data_tipos_analise,
        data_representantes,
        data_cliente,
        isEdicao,
        analises,
        erros,
        limitesColeta,
        modo,
    } = useFormOsContext()

    //#region BLOQUEIO DO TALHÃO
    // Regras portadas da versão em produção. O talhão define a malha de pontos e o
    // contorno da OS: trocá-lo depois invalidaria os pontos e quebraria a
    // sincronização com o app. 
    const possuiMalha = (analises?.malha?.length ?? 0) > 0
    const semPropriedade = campos.propriedade_id === null

    const motivoTalhaoBloqueado = semPropriedade
        ? 'Selecione uma propriedade para escolher o talhão.'
        : isEdicao
            ? 'O talhão não pode ser alterado ao editar: os pontos e o contorno já coletados estão vinculados a ele.'
            : possuiMalha
                ? 'A malha de pontos já foi importada e está vinculada a este talhão. Remova a malha para trocar o talhão.'
                : undefined

    const talhaoBloqueado = motivoTalhaoBloqueado !== undefined

    //#region BLOQUEIO DA PROPRIEDADE
    // Mesma razão do talhão: a malha de pontos e o contorno da OS pertencem a
    // esta propriedade. Trocá-la depois de criada invalidaria tudo o que já foi
    // coletado, então o campo fica somente-leitura ao editar.
    const motivoPropriedadeBloqueada = isEdicao
        ? 'A propriedade não pode ser alterada ao editar: os pontos e o contorno já coletados pertencem a ela.'
        : undefined

    //#region BLOQUEIO DO "COLETADO EM"
    // A data efetiva da coleta só faz sentido depois que a coleta aconteceu. O campo
    // fica visível ao editar (como na produção), mas só é editável no status COLETADA.
    const coletadaEmBloqueado = campos.status_atual_id !== STATUS_COLETADA

    const motivoColetadaEmBloqueado = coletadaEmBloqueado
        ? `A data da coleta só pode ser informada quando a OS estiver no status ${statusNomes[STATUS_COLETADA]}.`
        : undefined
    //#endregion

    // A entrega ao laboratório só existe depois da coleta: aparece em COLETADA e
    // permanece em ENVIADO PARA ANÁLISE. Diferente do "Coletado em", é opcional.
    const mostrarDataEntregaLab = campos.status_atual_id === STATUS_COLETADA || campos.status_atual_id === STATUS_ENVIADO_ANALISE;

    /** Posição da área de amostra nas tabs (-1 vira 0: nenhuma selecionada). */
    const indexAreaAmostra = Math.max(AREAS_AMOSTRA.findIndex((area) => area.descricao === campos.area_amostra_laudo), 0)

    /**
     * Mesma regra do modal de linha do tempo: ANALISADA é atribuído pelo backend
     * ao concluir o processamento, e o status atual não é uma alteração.
     */
    const status_desabilitados = useMemo(
        () => statusDesabilitados(campos.status_atual_id),
        [campos.status_atual_id]
    )

    //#region ESTADO LOCAL DE ABERTURA DOS SELECTS
    const [openStatus, setOpenStatus] = useState(false)
    const [openRepresentante, setOpenRepresentante] = useState(false)
    const [openTipoAnalise, setOpenTipoAnalise] = useState(false)
    const [openPropriedade, setOpenPropriedade] = useState(false)
    const [openTalhao, setOpenTalhao] = useState(false)
    const [openLaboratorio, setOpenLaboratorio] = useState(false)
    const [openSafra, setOpenSafra] = useState(false)
    const [openResponsaveis, setOpenResponsaveis] = useState(false)

    const ehGrupo = data_cliente?.tipo === 'G'

    /** DatePicker do antd entrega um Dayjs; guardamos como ISO string (ou '') no estado. */
    const aoMudarData = (chave: 'data_da_os' | 'coleta_liberada' | 'coletada_em' | 'data_entrega_lab') => (valor: any) => {
        setCampo(chave, valor ? dayjs(valor).toISOString() : '')
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Render condition={isEdicao}>
                <Select
                    label='Status atual'
                    placeholder='Selecione o status atual'
                    icon={statusIcones[campos.status_atual_id as keyof typeof statusIcones]}
                    value={campos.status_atual}
                    onChange={(v) => setCampo('status_atual', v as string)}
                    onChangeID={(id) => setCampo('status_atual_id', id as number)}
                    open={openStatus}
                    trigger={setOpenStatus}
                    items={dados_status}
                    descricaoItem='nome'
                    idItem='cod'
                    disabledItens={status_desabilitados}
                    obrigatorio
                />
            </Render>

            <Render condition={ehGrupo}>
                <Select
                    label='Representante'
                    value={campos.representante_selecionado}
                    onChange={(v) => setCampo('representante_selecionado', v as string)}
                    onChangeID={(id) => setCampo('representante_selecionado_id', id as number)}
                    open={openRepresentante}
                    trigger={setOpenRepresentante}
                    items={data_representantes}
                    descricaoItem='nome'
                    idItem='cod'
                    error={!!erros.representante}
                    errorMessage={erros.representante}
                />
            </Render>

            {/* Tipo da análise é definido pelo submódulo ativo (ex: Solo) — vem
                pré-selecionado e travado; não é escolha do usuário. 
                POR ENQUANTO SÓ EXISTE O MÓDULO DE SOLO - 12/08/26
                QUANDO LIBERAR OS DEMAIS MÓDULOS, ESSE SELECT PODERÁ EXISTIR
                */}
            {/* <Select
                label='Tipo da análise'
                placeholder='Selecione o tipo da análise'
                icon={faFlask}
                value={campos.tipo_de_analise}
                onChange={(v) => setCampo('tipo_de_analise', v as string)}
                onChangeID={(id) => setCampo('tipo_de_analise_id', id as number)}
                open={openTipoAnalise}
                trigger={setOpenTipoAnalise}
                items={data_tipos_analise}
                descricaoItem='nome'
                idItem='cod'
                disabled
                obrigatorio
            /> */}

            <DatePickerCustom
                label='Data O.S'
                icon={faCalendar}
                value={campos.data_da_os ? dayjs(campos.data_da_os) : null}
                onChange={aoMudarData('data_da_os')}
                width={150}
                obrigatorio
                error={!!erros.data_da_os}
                errorMessage={erros.data_da_os}
            />

            {/* Entrega das amostras ao laboratório. Opcional — o laboratório pode
                    receber as amostras dias depois da coleta, então a data nem sempre
                    é conhecida no momento em que a OS muda de status. */}
            <Render condition={mostrarDataEntregaLab}>
                <DatePickerCustom
                    label='Data de entrega para laboratório'
                    icon={faCalendar}
                    value={campos.data_entrega_lab ? dayjs(campos.data_entrega_lab) : null}
                    onChange={aoMudarData('data_entrega_lab')}
                    width={180}
                    opcional
                />
            </Render>

            <div className='detalhe__propriedade' style={{ marginTop: 10 }}>
                {/* Mesmo padrão do talhão: o tooltip explica por que o campo está
                    travado — sem ele o usuário só vê um select inerte. */}
                <Tooltip title={motivoPropriedadeBloqueada} placement='topLeft'>
                    <span style={{ display: 'block', cursor: isEdicao ? 'not-allowed' : undefined }}>
                        <Select
                            label='Propriedade'
                            placeholder='Selecione a propriedade'
                            icon={faVihara}
                            value={campos.propriedade}
                            onChange={(v) => selecionarPropriedade(v as string)}
                            onChangeID={(id) => selecionarPropriedadeId(id as number)}
                            open={openPropriedade}
                            trigger={setOpenPropriedade}
                            items={propriedades_ativas}
                            descricaoItem='nome'
                            idItem='cod'
                            disabled={isEdicao}
                            loading={carregando_propriedades}
                            obrigatorio
                            error={!!erros.propriedade}
                            errorMessage={erros.propriedade}
                        />
                    </span>
                </Tooltip>
                <div style={{ marginTop: 6 }}>
                    <Tag style={{ border: '1px solid #0958D9', color: '#0958D9' }} color='#E6F4FF'>
                        Matrícula: {matricula}
                    </Tag>
                    <Tag style={{ border: '1px solid #B7EB8F', color: '#52C41A' }} color='#F6FFED'>
                        Área: {area_propriedade} ha
                    </Tag>
                </div>
            </div>

            <Input
                label='Outros solicitantes'
                icon={faUsers}
                placeholder='Digite os outros solicitantes'
                value={campos.outros_solicitantes}
                onChange={(v) => setCampo('outros_solicitantes', v)}
            />

            {/* O tooltip explica POR QUE o campo está travado — sem ele o usuário
                só vê um select inerte. Envolve o Select porque o componente não
                expõe prop de tooltip para o campo inteiro. */}
            <Tooltip title={motivoTalhaoBloqueado} placement='topLeft'>
                <span style={{ display: 'block', cursor: talhaoBloqueado ? 'not-allowed' : undefined }}>
                    <Select
                        label='Talhão'
                        placeholder='Selecione o talhão'
                        icon={faMap}
                        value={campos.talhao}
                        onChange={(v) => setCampo('talhao', v as string)}
                        onChangeID={(id) => setCampo('talhao_id', id as number)}
                        open={openTalhao}
                        trigger={setOpenTalhao}
                        items={talhoes}
                        descricaoItem='nome'
                        idItem='cod'
                        hasTalhaoSVG
                        talhaoSvgPath={['coordenadas']}
                        maxPontosSvg={200}
                        disabled={talhaoBloqueado}
                        loading={carregando_talhoes}
                        obrigatorio={modo === 'criar'}
                        error={!!erros.talhao}
                        errorMessage={erros.talhao}
                    />
                </span>
            </Tooltip>

            <Select
                label='Laboratório'
                placeholder='Selecione o laboratório'
                icon={faFlask}
                value={campos.laboratorio}
                onChange={(v) => setCampo('laboratorio', v as string)}
                onChangeID={(id) => setCampo('laboratorio_id', id as number)}
                open={openLaboratorio}
                trigger={setOpenLaboratorio}
                items={data_laboratorios}
                descricaoItem='nome'
                idItem='cod'
                obrigatorio
                error={!!erros.laboratorio}
                errorMessage={erros.laboratorio}
            />

            <Select
                label='Safra consultoria'
                placeholder='Selecione a safra consultoria'
                icon={faSeedling}
                value={campos.safra_consultoria}
                onChange={(v) => setCampo('safra_consultoria', v as string)}
                onChangeID={(id) => setCampo('safra_consultoria_id', id as number)}
                open={openSafra}
                trigger={setOpenSafra}
                items={[...(data_safra_consultoria ?? [])].sort((a, b) => b.cod - a.cod)}
                descricaoItem='nome'
                idItem='cod'
                obrigatorio
                error={!!erros.safra_consultoria}
                errorMessage={erros.safra_consultoria}
            />

            <div>
                <label
                    style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#2D3436',
                        display: 'inline-flex',
                        gap: 5,
                    }}
                >
                    Área de amostra no laudo
                </label>
                <Tabs
                    tabs={AREAS_AMOSTRA}
                    selecionado={campos.area_amostra_laudo}
                    // O destaque verde é posicionado por `indexInicial`; sem ele fica
                    // preso em 0 e não acompanha a OS hidratada (ex: 20 ha).
                    indexInicial={indexAreaAmostra}
                    onChange={(v) => setCampo('area_amostra_laudo', v as any)}
                    trackWidth={55}
                    width={120}
                />
            </div>

            <SelectMulti
                label='Responsáveis pela coleta'
                placeholder='Selecione o responsável pela coleta'
                icon={faUsersLine}
                open={openResponsaveis}
                trigger={setOpenResponsaveis}
                items={data_equipe}
                descricaoItem='usuario_nome'
                idItem='cod_func'
                selecteds={campos.responsaveis_selecionados}
                onChange={setResponsaveis}
                obrigatorio
                error={!!erros.responsaveis}
                errorMessage={erros.responsaveis}
            />

            <div style={{ display: 'flex', gap: 12 }}>
                <DatePickerCustom
                    label='Coleta liberada'
                    icon={faCalendar}
                    value={campos.coleta_liberada ? dayjs(campos.coleta_liberada) : null}
                    onChange={aoMudarData('coleta_liberada')}
                    width={180}
                    obrigatorio
                    error={!!erros.coleta_liberada}
                    errorMessage={erros.coleta_liberada}
                />
                {/* Data efetiva da coleta: só faz sentido depois que a coleta aconteceu.
                    Fica visível ao editar (como na produção), mas só é editável no
                    status COLETADA — o tooltip explica o motivo do bloqueio. */}
                <Render condition={isEdicao}>
                    <Tooltip title={motivoColetadaEmBloqueado} placement='topLeft'>
                        <span style={{ cursor: coletadaEmBloqueado ? 'not-allowed' : undefined }}>
                            {/* Limitado ao intervalo em que houve coleta no talhão
                                (`datas_coleta` da OS): informar data fora dele
                                registraria uma coleta que não aconteceu. */}
                            <DatePickerCustom
                                label='Coletado em'
                                icon={faCalendar}
                                value={campos.coletada_em ? dayjs(campos.coletada_em) : null}
                                onChange={aoMudarData('coletada_em')}
                                width={190}
                                desabilitado={coletadaEmBloqueado}
                                obrigatorio={!coletadaEmBloqueado}
                                minDate={limitesColeta?.min}
                                maxDate={limitesColeta?.max}
                                info_message='Apenas o intervalo entre a primeira e última data de coleta informado no aplicativo estão disponíveis para seleção. O campo já vem preenchido com a última data de coleta!'
                                error={!!erros.coletada_em}
                                errorMessage={erros.coletada_em}
                            />
                        </span>
                    </Tooltip>
                </Render>
            </div>
        </div>
    )
})

CamposFormulario.displayName = 'CamposFormulario'
